import React, { useState, useMemo } from 'react';
import { ChevronDown, ChevronRight } from 'lucide-react';

/**
 * CollapsibleSection
 * A single professional expandable/collapsible section with an icon,
 * numbered order, title, optional subtitle, and animated content area.
 *
 * Props:
 *  - index:     integer position (1-based) used for the numbered chip
 *  - icon:      lucide-react icon component (defaults to a document icon set by parent)
 *  - title:     section title (string)
 *  - subtitle:  optional secondary label shown next to the title
 *  - defaultOpen: whether the section is open on first render
 *  - accent:    tailwind color token used for the icon chip (e.g. 'indigo', 'emerald')
 *  - children:  section content (usually text/markdown/table)
 *  - testId:    data-testid on the outer wrapper
 */
export const CollapsibleSection = ({
  index,
  icon: Icon,
  title,
  subtitle,
  defaultOpen = false,
  accent = 'indigo',
  children,
  testId,
}) => {
  const [open, setOpen] = useState(defaultOpen);

  const accentClasses = {
    indigo:   { chipBg: 'bg-indigo-100',   chipText: 'text-indigo-700',   ring: 'ring-indigo-200' },
    emerald:  { chipBg: 'bg-emerald-100',  chipText: 'text-emerald-700',  ring: 'ring-emerald-200' },
    amber:    { chipBg: 'bg-amber-100',    chipText: 'text-amber-700',    ring: 'ring-amber-200' },
    rose:     { chipBg: 'bg-rose-100',     chipText: 'text-rose-700',     ring: 'ring-rose-200' },
    sky:      { chipBg: 'bg-sky-100',      chipText: 'text-sky-700',      ring: 'ring-sky-200' },
    violet:   { chipBg: 'bg-violet-100',   chipText: 'text-violet-700',   ring: 'ring-violet-200' },
    slate:    { chipBg: 'bg-slate-100',    chipText: 'text-slate-700',    ring: 'ring-slate-200' },
  };
  const a = accentClasses[accent] || accentClasses.indigo;

  return (
    <div
      className={`bg-white border border-gray-200 rounded-xl shadow-sm hover:shadow transition-shadow overflow-hidden ${open ? `ring-1 ${a.ring}` : ''}`}
      data-testid={testId}
    >
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        className="w-full flex items-center gap-3 px-4 py-3 text-left hover:bg-gray-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-400/40 transition-colors"
      >
        {typeof index === 'number' && (
          <span className={`shrink-0 inline-flex items-center justify-center w-8 h-8 rounded-lg text-xs font-semibold ${a.chipBg} ${a.chipText}`}>
            {index}
          </span>
        )}
        {Icon && (
          <span className={`shrink-0 ${a.chipText}`}>
            <Icon className="w-4 h-4" />
          </span>
        )}
        <span className="flex-1 min-w-0">
          <span className="block text-sm sm:text-base font-semibold text-gray-900 truncate">
            {title}
          </span>
          {subtitle && (
            <span className="block text-xs text-gray-500 mt-0.5 truncate">
              {subtitle}
            </span>
          )}
        </span>
        <span className="shrink-0 text-gray-400">
          {open ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
        </span>
      </button>
      {open && (
        <div className="px-5 pb-5 pt-1 border-t border-gray-100 bg-gray-50/60">
          <div className="pt-4">
            {children}
          </div>
        </div>
      )}
    </div>
  );
};

/**
 * SectionGroup
 * Convenience wrapper for a set of collapsible sections with
 * "Expand all / Collapse all" controls.
 */
export const SectionGroup = ({ children, testId }) => {
  return (
    <div className="space-y-3" data-testid={testId}>
      {children}
    </div>
  );
};

/**
 * ExpandAllControls
 * Renders "Expand all | Collapse all" links; caller controls the shared open state.
 */
export const ExpandAllControls = ({ onExpandAll, onCollapseAll }) => (
  <div className="flex items-center justify-end gap-3 text-xs mb-2">
    <button
      type="button"
      onClick={onExpandAll}
      className="text-indigo-700 hover:text-indigo-900 hover:underline font-medium"
      data-testid="expand-all-btn"
    >
      Expand all
    </button>
    <span className="text-gray-300">|</span>
    <button
      type="button"
      onClick={onCollapseAll}
      className="text-indigo-700 hover:text-indigo-900 hover:underline font-medium"
      data-testid="collapse-all-btn"
    >
      Collapse all
    </button>
  </div>
);

/* --------------------------------------------------------------------------
 * Section-parsing utility for free-form AI narrative reports
 * (used by "Why" and "Next Steps" tabs where content is a plain text blob).
 * -------------------------------------------------------------------------- */

const HEADING_MATCHERS = [
  (line) => {
    const m = line.trim().match(/^\*\*(.+?)\*\*\s*:?\s*$/);
    return m ? m[1].trim().replace(/:$/, '') : null;
  },
  (line) => {
    const m = line.trim().match(/^#{1,4}\s+(.+?)\s*:?\s*$/);
    return m ? m[1].trim().replace(/:$/, '') : null;
  },
  (line) => {
    const t = line.trim();
    const m = t.match(/^(\d{1,2})[.)]\s+(.+?)\s*:?\s*$/);
    if (!m) return null;
    const title = m[2].trim();
    if (title.length > 90) return null;
    if (/[.!?]$/.test(title)) return null;
    return `${m[1]}. ${title.replace(/:$/, '')}`;
  },
  (line) => {
    const t = line.trim().replace(/:$/, '');
    if (t.length < 4 || t.length > 90) return null;
    const words = t.split(/\s+/);
    if (words.length < 2) return null;
    const letters = t.replace(/[^A-Za-z]/g, '');
    if (letters.length < 3) return null;
    if (letters !== letters.toUpperCase()) return null;
    return t;
  },
];

const detectHeading = (line) => {
  for (const test of HEADING_MATCHERS) {
    const h = test(line);
    if (h) return h;
  }
  return null;
};

export const parseNarrativeSections = (raw) => {
  if (!raw || typeof raw !== 'string') return [];
  const lines = raw.split(/\r?\n/);
  const sections = [];
  let current = null;
  const preamble = [];

  for (let i = 0; i < lines.length; i++) {
    const line = lines[i];
    const heading = detectHeading(line);
    if (heading) {
      if (current) sections.push(current);
      current = { title: heading, body: [] };
    } else if (current) {
      current.body.push(line);
    } else {
      preamble.push(line);
    }
  }
  if (current) sections.push(current);

  if (sections.length === 0) return [];

  const preambleText = preamble.join('\n').trim();
  if (preambleText.length > 0) {
    sections.unshift({ title: 'Overview', body: preambleText.split('\n') });
  }

  return sections
    .map((s) => ({
      title: s.title,
      body: s.body.join('\n').replace(/^\n+|\n+$/g, ''),
    }))
    .filter((s) => s.body.trim().length > 0 || s.title);
};

/**
 * CollapsibleNarrative
 * Renders a plain-text/markdown-like report as a stack of expandable sections.
 * Falls back to a single preformatted block if no headings can be detected,
 * so no content is ever lost.
 */
export const CollapsibleNarrative = ({ content, icon: Icon, accent = 'indigo', testId }) => {
  const sections = useMemo(() => parseNarrativeSections(content), [content]);
  const [allOpenKey, setAllOpenKey] = useState(0);
  const [defaultOpen, setDefaultOpen] = useState(false);

  if (!content) return null;

  if (sections.length === 0) {
    return (
      <div
        className="prose prose-sm max-w-none whitespace-pre-line text-gray-800 bg-gray-50 p-4 rounded border"
        data-testid={testId}
      >
        {content}
      </div>
    );
  }

  return (
    <div data-testid={testId}>
      <ExpandAllControls
        onExpandAll={() => { setDefaultOpen(true); setAllOpenKey((k) => k + 1); }}
        onCollapseAll={() => { setDefaultOpen(false); setAllOpenKey((k) => k + 1); }}
      />
      <SectionGroup>
        {sections.map((s, i) => (
          <CollapsibleSection
            key={`${allOpenKey}-${i}`}
            index={i + 1}
            icon={Icon}
            title={s.title}
            defaultOpen={defaultOpen || i === 0}
            accent={accent}
            testId={`narrative-section-${i}`}
          >
            <div className="prose prose-sm max-w-none whitespace-pre-line text-gray-800 leading-relaxed">
              {s.body}
            </div>
          </CollapsibleSection>
        ))}
      </SectionGroup>
    </div>
  );
};

export default CollapsibleSection;
